'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: string;
  amount: number;
  onConfirm: (paymentMethod: string, isAnonymous: boolean) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  category,
  amount,
  onConfirm,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);

  const paymentMethods = [
    { id: 'card', name: 'Credit/Debit Card', icon: 'CreditCardIcon' },
    { id: 'paypal', name: 'PayPal', icon: 'CurrencyDollarIcon' },
    { id: 'bank', name: 'Bank Transfer', icon: 'BuildingLibraryIcon' },
    { id: 'venmo', name: 'Venmo', icon: 'DevicePhoneMobileIcon' },
  ];

  const handleConfirm = () => {
    if (selectedMethod) {
      onConfirm(selectedMethod, isAnonymous);
      setSelectedMethod('');
      setIsAnonymous(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-sacred-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="font-playfair text-2xl font-semibold text-foreground">
            Complete Contribution
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:bg-surface transition-colors duration-200"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className="bg-surface rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="font-inter text-sm text-muted-foreground">
                Contributing to:
              </span>
              <span className="font-inter text-sm font-medium text-foreground">
                {category}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-inter text-sm text-muted-foreground">
                Amount:
              </span>
              <span className="font-playfair text-2xl font-bold text-primary">
                ${amount.toLocaleString('en-US')}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <label className="font-inter text-sm font-medium text-foreground block">
              Select Payment Method
            </label>
            <div className="grid grid-cols-1 gap-2">
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  className={`flex items-center space-x-3 p-4 rounded-md border transition-all duration-200 ${
                    selectedMethod === method.id
                      ? 'bg-primary/10 border-primary' :'bg-surface border-border hover:bg-primary/5'
                  }`}
                >
                  <Icon
                    name={method.icon as any}
                    size={24}
                    className={
                      selectedMethod === method.id
                        ? 'text-primary' :'text-muted-foreground'
                    }
                  />
                  <span
                    className={`font-inter text-sm font-medium ${
                      selectedMethod === method.id
                        ? 'text-primary' :'text-foreground'
                    }`}
                  >
                    {method.name}
                  </span>
                  {selectedMethod === method.id && (
                    <Icon
                      name="CheckCircleIcon"
                      size={20}
                      className="text-primary ml-auto"
                      variant="solid"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 bg-surface rounded-lg border border-border">
            <input
              type="checkbox"
              id="anonymous"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="mt-1 w-4 h-4 text-primary bg-input border-border rounded focus:ring-2 focus:ring-ring"
            />
            <label
              htmlFor="anonymous"
              className="font-inter text-sm text-foreground cursor-pointer"
            >
              Make this contribution anonymous. Your name will not be displayed
              publicly, but the contribution will still count toward the total.
            </label>
          </div>

          <div className="bg-accent/10 rounded-lg p-4 border border-accent/20">
            <div className="flex items-start space-x-3">
              <Icon
                name="ShieldCheckIcon"
                size={20}
                className="text-accent flex-shrink-0 mt-0.5"
              />
              <p className="font-inter text-xs text-foreground leading-relaxed">
                Your contribution is secure and will be processed through our
                trusted payment partners. All transactions are encrypted and
                protected.
              </p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex items-center space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 rounded-md font-inter text-sm font-medium text-foreground bg-surface hover:bg-muted border border-border transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedMethod}
            className={`flex-1 px-6 py-3 rounded-md font-inter text-sm font-medium transition-all duration-200 ${
              selectedMethod
                ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            Confirm Contribution
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;